<template>
  <v-app>
    <v-main>
      <div v-if="ready">
        <div
          v-if="!this.$vuetify.breakpoint.mdAndUp"
          class="sm-goal d-flex flex-column align-items-center pt-6 pb-12 mb-4"
        >
          <div class="total-raised d-flex flex-column align-items-center mb-3">
            <div class="white--text total-raised__label">
              {{ $t('action.fundraiserGoal.totalRaised', locale) }}
            </div>
            <currency-odometer
              class="white--text"
              :amount="convertedAmountRaised"
              :currency-config="currentCurrencyConfig"
            />
          </div>
          <div class="progress-circle d-flex justify-content-center">
            <goal-progress-circle
              :amount="convertedAmountRaised"
              :goal="convertedGoal"
              :currency-config="currentCurrencyConfig"
              :locale="locale"
            >
              <div
                class="d-flex flex-column justify-center align-items-center"
                style="font-size: 24px"
              >
                <div class="goal-label">
                  {{ $t('action.fundraiserGoal.goal', locale) }}
                </div>
                <div class="d-flex align-items-center font-weight-bold">
                  <currency-formatted
                    class="font-weight-bold"
                    :value="convertedGoal"
                    :currency-config="currentCurrencyConfig"
                    :locale="locale"
                  />
                </div>
              </div>
            </goal-progress-circle>
          </div>
        </div>
        <div v-if="this.$vuetify.breakpoint.mdAndUp" class="d-flex justify-content-center">
          <div class="lg-goal d-flex justify-content-center align-items-center py-6">
            <div class="goal d-flex flex-column align-items-center mb-3">
              <div class="white--text goal__label">
                {{ $t('action.fundraiserGoal.goal', locale) }}
              </div>
              <currency-formatted
                class="white--text font-weight-bold"
                style="font-size: 36px"
                :value="convertedGoal"
                :currency-config="currentCurrencyConfig"
                :locale="locale"
              />
            </div>
            <goal-progress-circle
              slot-classes="font-weight-bold"
              :amount="convertedAmountRaised"
              :goal="convertedGoal"
              :currency-config="currentCurrencyConfig"
              :locale="locale"
              :circle-line-width="12"
              :circle-size="lgGoalProgressCircleSize"
              :style="{ minWidth: `${lgGoalProgressCircleSize}px` }"
              :slot-styles="{
                fontSize: '48px'
              }"
            />
            <div class="total-raised d-flex flex-column align-items-center mb-3">
              <div class="white--text total-raised__label">
                {{ $t('action.fundraiserGoal.totalRaised', locale) }}
              </div>
              <currency-odometer
                class="white--text"
                :amount="convertedAmountRaised"
                :currency-config="currentCurrencyConfig"
              />
            </div>
          </div>
        </div>
      </div>
    </v-main>
  </v-app>
</template>

<script>
import { mapGetters, mapState } from 'vuex'

import CurrencyOdometer from './shared-vue-components/FundraiserGoal/components/CurrencyOdometer'
import GoalProgressCircle from './shared-vue-components/FundraiserGoal/components/GoalProgressCircle'
import CurrencyFormatted from './shared-vue-components/FundraiserGoal/components/CurrencyFormatted'

export default {
  name: 'ResponsiveGoalApp',
  components: {
    CurrencyOdometer,
    GoalProgressCircle,
    CurrencyFormatted
  },
  data: () => ({
    amountRaisedUSD: 0,
    lgGoalProgressCircleSize: 200
  }),
  computed: {
    ready() {
      return this.formConfig !== null
    },
    messages() {
      return this.socket.messages
    },
    convertedAmountRaised() {
      return this.convertToCurrentCurrency(this.amountRaisedUSD, 'USD', this.currentCurrencyConfig)
    },
    convertedGoal() {
      return this.convertToCurrentCurrency(
        this.formConfig.fundraiser_goal_local,
        this.formConfig.local_currency_code,
        this.currentCurrencyConfig
      )
    },
    ...mapState('actionForm', ['locale', 'formConfig', 'stats', 'socket']),
    ...mapGetters('actionForm', ['currentCurrencyConfig', 'currencyConfigLookup'])
  },
  watch: {
    ready(isReady) {
      if (isReady) {
        this.amountRaisedUSD = this.formConfig.fundraiser_total_raised_usd
      }
    },
    messages(queue) {
      const messages = Array.from(queue)
      const message = messages.pop()
      const { donation_inc } = message
      if (donation_inc) {
        const { baseAmount } = donation_inc
        const amount = Number(baseAmount)
        if (!isNaN(amount)) {
          this.amountRaisedUSD += amount
        }
      }
    }
  },
  methods: {
    // Convert the amount to the base currency in the currency rates lookup
    convertToUSD(amount, currencyCode) {
      const { currency_rate: currencyRate } = this.formConfig
      console.assert(
        currencyRate.base_currency.toLowerCase() === 'usd',
        'Base currency is not USD. Currency conversion for displayed values will be inaccurate!'
      )
      return currencyCode === currencyRate.base_currency
        ? amount
        : amount / currencyRate.rates[currencyCode]
    },
    // Convert the amount to the currency currently selected on the donation form
    convertToCurrentCurrency(amount, currencyCode, currentCurrencyConfig) {
      const { currency_rate: currencyRate } = this.formConfig
      const { code: currentCurrencyCode } = currentCurrencyConfig

      return currentCurrencyCode === currencyCode
        ? amount
        : this.convertToUSD(amount, currencyCode) * currencyRate.rates[currentCurrencyCode]
    }
  }
}
</script>

<!-- global app styles -->
<style lang="scss">
.v-application {
  background-color: transparent !important;
}
.v-application--wrap {
  min-height: unset !important;
}

::v-deep .odometer-digit-inner {
  display: flex;
  justify-content: end;
}
</style>

<!-- scoped component styles -->
<style lang="scss" scoped>
.sm-goal {
  .total-raised__label {
    font-size: 18px;
  }
}
.lg-goal {
  .total-raised__label,
  .goal__label {
    font-size: 24px;
  }
  .goal,
  .total-raised {
    min-width: 400px;
    width: 100%;
  }
}
</style>
